import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import parse from "date-fns/parse";
import { resources } from "@constants/hardcoded-strings";
import { SERVER_DATE_FORMAT } from "@constants/date-formats";
import { TimeRangeTypes } from "@enums/time-range-types";
import { CustomizeBySearchTypes } from "@enums/customize-by-search-types";
import { TableSettingsTableTypes } from "@enums/table-settings-table-types";
import { ApplicationState } from "@idw-types/state/application-state";
import { AdaptiveAuthenticationSearchFiltersState } from "@idw-types/state/adaptive-authentication-state";
import { resetTableSettingsTableState } from "@redux/actions/table-settings-actions";
import { setAdaptiveAuthenticationSearchFilters } from "@redux/actions/adaptive-authentication-actions";
import { initialAdaptiveAuthenticationState } from "@redux/reducers/adaptive-authentication-reducer";
import {
    getAdaptiveAuthenticationReferenceDataReduxRequest,
    getAdaptiveAuthenticationResultsReduxRequest
} from "@redux-requests/adaptive-authentication";
import { isStringTrue } from "@iddataweb/idw-common-ui/utils";
import { ContentPageHeader } from "@containers/common/content-page-header/content-page-header";
import { CardContainer } from "@containers/common/card-container/card-container";
import { LoadingIndicator } from "@containers/common/loading-indicator/loading-indicator";
import { ContentPageBody } from "@containers/common/content-page-body/content-page-body";
import { AdaptiveAuthenticationSearchForm } from "./adaptive-authentication-search-form/adaptive-authentication-search-form";
import { AdaptiveAuthenticationTableContainer } from "./adaptive-authentication-table-container/adaptive-authentication-table-container";

export const AdapativeAuthenticationContainer = () => {
    const [searchParams] = useSearchParams();
    const dispatch = useDispatch();
    const {
        referenceData,
        searchFilters,
        searchResults,
        adaptiveAuthenticationTableState
    } = useSelector((state: ApplicationState) => ({
        referenceData: state.adaptiveAuthentication.referenceData,
        searchFilters: state.adaptiveAuthentication.searchFilters,
        searchResults: state.adaptiveAuthentication.searchResults,
        adaptiveAuthenticationTableState: state.tableSettings.adaptiveAuthentication
    }));

    const [shouldLoadReferenceData, setShouldLoadReferenceData] = useState(true);

    useEffect(() => {
        if (!referenceData.isLoading && shouldLoadReferenceData) {
            setShouldLoadReferenceData(false);
            getAdaptiveAuthenticationReferenceDataReduxRequest(dispatch)();
        }
    }, [referenceData.isLoading, shouldLoadReferenceData]);

    const isReferenceDataPresent = useMemo(() => {
        return !!(
            referenceData.adaptiveAuthReportConfigs &&
            referenceData.companies &&
            referenceData.relyingParties &&
            referenceData.rpServices &&
            referenceData.workflows
        );
    }, [
        referenceData.adaptiveAuthReportConfigs,
        referenceData.companies,
        referenceData.relyingParties,
        referenceData.rpServices,
        referenceData.workflows
    ]);

    const isLoading = useMemo(() => {
        return referenceData.isLoading;
    }, [referenceData.isLoading]);

    const isLoaded = useMemo(() => {
        return !isLoading && isReferenceDataPresent && !shouldLoadReferenceData;
    }, [isLoading, isReferenceDataPresent, shouldLoadReferenceData]);

    useEffect(() => {
        if (Array.from(searchParams.entries()).length > 0 && isLoaded) {
            const shouldSearchOnLoad = isStringTrue(searchParams.get("shouldSearchOnLoad"));

            const newSearchFilters: AdaptiveAuthenticationSearchFiltersState = {
                customizeBySearchType: (searchParams.get("customizeBySearchType") || initialAdaptiveAuthenticationState.searchFilters.customizeBySearchType) as CustomizeBySearchTypes,
                companyId: searchParams.get("companyId") || initialAdaptiveAuthenticationState.searchFilters.companyId,
                rpServiceId: searchParams.get("rpServiceId") || initialAdaptiveAuthenticationState.searchFilters.rpServiceId,
                workflowId: searchParams.get("workflowId") || initialAdaptiveAuthenticationState.searchFilters.workflowId,
                timeRangeType: (searchParams.get("timeRangeType") || initialAdaptiveAuthenticationState.searchFilters.timeRangeType) as TimeRangeTypes,
                startDate: searchParams.has("startDate")
                    ? parse(searchParams.get("startDate"), SERVER_DATE_FORMAT, new Date())
                    : initialAdaptiveAuthenticationState.searchFilters.startDate,
                endDate: searchParams.has("endDate")
                    ? parse(searchParams.get("endDate"), SERVER_DATE_FORMAT, new Date())
                    : initialAdaptiveAuthenticationState.searchFilters.endDate
            };

            if (shouldSearchOnLoad) {
                handleSearch(newSearchFilters);
            } else {
                dispatch(setAdaptiveAuthenticationSearchFilters(newSearchFilters));
            }
        }
    }, [searchParams, isLoaded]);

    const handleSearch = async (newSearchFilters: AdaptiveAuthenticationSearchFiltersState) => {
        const { companyId, rpServiceId, workflowId, startDate, endDate } = newSearchFilters;
        dispatch(resetTableSettingsTableState(TableSettingsTableTypes.ADAPTIVE_AUTHENTICATION));
        dispatch(setAdaptiveAuthenticationSearchFilters(newSearchFilters));
        getAdaptiveAuthenticationResultsReduxRequest(dispatch)(
            companyId,
            rpServiceId,
            workflowId,
            startDate,
            endDate
        );
    };

    return (
        <CardContainer>
            <ContentPageHeader title={resources.pages.adaptiveAuthentication.header} />
            <ContentPageBody isMaxWidth={true}>
                {isLoading && (
                    <LoadingIndicator title={resources.common.loadingIndicatorText} />
                )}
                {isLoaded && (
                    <AdaptiveAuthenticationSearchForm
                        requestInProgress={searchResults.isLoading}
                        adaptiveAuthReportConfigs={referenceData.adaptiveAuthReportConfigs}
                        companies={referenceData.companies}
                        relyingParties={referenceData.relyingParties}
                        rpServices={referenceData.rpServices}
                        workflows={referenceData.workflows}
                        searchFilters={searchFilters}
                        handleSearch={handleSearch}
                    />
                )}
                {isLoaded && !searchResults.isLoading && searchResults.adaptiveAuthenticationResults && (
                    <AdaptiveAuthenticationTableContainer
                        adaptiveAuthenticationResults={searchResults.adaptiveAuthenticationResults}
                        tableState={adaptiveAuthenticationTableState}
                    />
                )}
                {isLoaded && searchResults.isLoading && (
                    <LoadingIndicator title={resources.common.loadingIndicatorText} />
                )}
            </ContentPageBody>
        </CardContainer>
    );
};